import type { OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  NgZone,
  Output,
  PLATFORM_ID,
  QueryList,
  ViewChild,
} from '@angular/core';
import { ContentSliderItemComponent } from './content-slider-item/content-slider-item.component';
import { isPlatformBrowser } from '@angular/common';

declare const window: HammerWindow;
interface HammerWindow extends Window {
  Hammer?: any;
}

@Component({
  selector: 'nmo-content-slider',
  templateUrl: './content-slider.component.html',
  styleUrls: ['./content-slider.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContentSliderComponent implements OnInit, OnChanges, OnDestroy {
  @ContentChildren(ContentSliderItemComponent)
  public _items: QueryList<ContentSliderItemComponent>;
  @Input() slides: HTMLElement[] = [];
  @Input() selectedIndex = 0;

  @Input() hasArrowNav = false;
  @Input() hasPointNav = true;
  @Input() autoslideOn = true;
  @Input() pauseAutoSlideOnHover = false;
  @Input() sliderDuration = 4000;
  @Input() arrowIconColor = '#008253';

  public timer: NodeJS.Timeout;

  @ViewChild('slideInteraction', { static: true }) slideInteraction: ElementRef;
  @Output() indexChanged: EventEmitter<number> = new EventEmitter<number>();
  private hammer: HammerWindow['Hammer'];

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    @Inject(PLATFORM_ID) private platformId: Object,
    private _zone: NgZone
  ) {}

  // life cycles

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      if (this.autoslideOn) {
        this.autoSlide();
      }

      this._zone.runOutsideAngular(() => {
        if (window?.Hammer) {
          this.hammer = new window.Hammer(this.slideInteraction.nativeElement);
          this.hammer.on('swipe', (event) => {
            this._zone.run(() => {
              this.handleSwipe(event);
            });
          });
        }
      });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (isPlatformBrowser(this.platformId) && changes.selectedIndex) {
      this.changeSlide(changes.selectedIndex.currentValue);
    }
  }

  ngOnDestroy() {
    if (this.hammer) {
      this.hammer.off('swipe');
    }
    this.clearAutoSlide();
  }

  // swipe movement

  private handleSwipe(event: any): void {
    this.clearAutoSlide();
    console.log(event);

    if (event.offsetDirection === 2) {
      this.handleSwipeRight();
    } else {
      this.handleSwipeLeft();
    }
  }

  private handleSwipeRight(): void {
    if (this._items.length === this.selectedIndex + 1) {
      this.changeSlide(0);
    } else {
      this.changeSlide(this.selectedIndex + 1);
    }
  }

  private handleSwipeLeft(): void {
    if (this.selectedIndex === 0) {
      if (this.slides.length === 0) {
        this.slides.length = this._items.length;
      }
      this.changeSlide(this.slides.length - 1);
    } else {
      this.changeSlide(this.selectedIndex - 1);
    }
  }

  // slider specific

  public changeSlide(i: string | number): void {
    if (typeof i === 'string') {
      if (i === 'prev' && this.selectedIndex !== 0) {
        this.selectedIndex -= 1;
      } else if (i === 'next' && this.selectedIndex + 1 !== this._items.length) {
        this.selectedIndex += 1;
      } else if (i === 'next' && this.selectedIndex + 1 === this._items.length) {
        this.selectedIndex = 0;
      }
    } else {
      this.selectedIndex = i;
    }

    setTimeout(() => {
      this._items.map((item: ContentSliderItemComponent, index: number) => {
        item.position = index - this.selectedIndex;
      });
    });

    this.indexChanged.emit(this.selectedIndex);
    this.changeDetectorRef.markForCheck();
  }

  public autoSlide(): void {
    this.timer = setInterval(() => {
      if (this._items.length === this.selectedIndex + 1) {
        this.changeSlide(0);
      } else {
        this.changeSlide(this.selectedIndex + 1);
      }
    }, this.sliderDuration);
  }

  public clearAutoSlide(): void {
    clearInterval(this.timer);
  }

  // hover pause specific

  public startAutoPlay(event: Event): void {
    event.stopPropagation();
    this.autoSlide();
  }

  public pauseAutoplay(event: Event): void {
    event.stopPropagation();
    this.clearAutoSlide();
  }
}
